/**
 * 提示语类型配置
 * 职责: 定义提示语类型、图标、描述、功能路径等元数据
 */
import React from 'react';
import {
    FileTextOutlined,
    BarChartOutlined,
    BulbOutlined,
    EditOutlined,
    TeamOutlined,
    EnvironmentOutlined,
    RocketOutlined,
} from '@ant-design/icons';
import type { TemplateType } from '../../../types/ai-analysis-types';

export interface PromptTypeConfig {
    type: TemplateType;
    label: string;
    icon: React.ReactNode;
    description: string;
    color: string;
    /** 功能所在路径，便于用户定位 */
    path?: string;
}

export const PROMPT_TYPES: PromptTypeConfig[] = [
    // ========== 关键词分析类 ==========
    {
        type: 'single_analysis',
        label: '单个关键词分析',
        icon: <FileTextOutlined />,
        description: '深度分析单个关键词的市场机会、竞争格局和投资回报。',
        color: '#1890ff',
        path: 'ABA分析 → 搜索表现 → AI分析',
    },
    {
        type: 'comparison',
        label: '多关键词对比',
        icon: <BarChartOutlined />,
        description: '对比多个关键词的核心指标，辅助选品决策和优先级排序。',
        color: '#722ed1',
        path: 'ABA分析 → 搜索表现 → AI分析',
    },
    {
        type: 'recommendation',
        label: '智能推荐',
        icon: <BulbOutlined />,
        description: '根据市场趋势和产品特性，智能推荐高价值关键词。',
        color: '#faad14',
        path: 'ABA分析 → 搜索表现 → AI分析',
    },
    {
        type: 'competitor_analysis',
        label: '竞品分析',
        icon: <BarChartOutlined />,
        description: '基于关键词搜索数据，深度分析市场竞争格局和进入策略。',
        color: '#f5222d',
        path: '运营策略 → SKU详情 → 竞品分析',
    },

    // ========== 文案生成类 ==========
    {
        type: 'copywriting',
        label: '文案生成 (FABE)',
        icon: <EditOutlined />,
        description: '基于 FABE 模型生成高转化率的产品卖点文案。',
        color: '#52c41a',
        path: '运营策略 → SKU详情 → 智能文案',
    },
    {
        type: 'product_description',
        label: '产品描述优化',
        icon: <EditOutlined />,
        description: '优化产品描述，使其更具吸引力和转化力。',
        color: '#13c2c2',
        path: '运营策略 → SKU详情 → 智能文案',
    },
    {
        type: 'desired_feeling',
        label: '期望感受生成',
        icon: <BulbOutlined />,
        description: '生成产品带给用户的期望感受关键词。',
        color: '#eb2f96',
        path: '运营策略 → SKU详情 → 产品策略',
    },
    {
        type: 'brand_story',
        label: '品牌故事创作',
        icon: <FileTextOutlined />,
        description: '基于产品特性创作引人入胜的品牌故事。',
        color: '#722ed1',
        path: '品牌管理 → 品牌详情',
    },
    {
        type: 'lifestyle_scene',
        label: '生活场景描述',
        icon: <FileTextOutlined />,
        description: '描绘产品在实际生活中的使用场景。',
        color: '#fa8c16',
        path: '运营策略 → SKU详情 → 智能文案',
    },

    // ========== 智能文案系统专用 ==========
    {
        type: 'smart_audience_analysis' as TemplateType,
        label: '目标人群智能分析',
        icon: <TeamOutlined />,
        description: '基于产品卖点和品牌调性，AI推荐多个细分目标人群。',
        color: '#1890ff',
        path: '运营策略 → SKU详情 → 智能文案 → Step 2',
    },
    {
        type: 'smart_scenario_recommendation' as TemplateType,
        label: '使用场景智能推荐',
        icon: <EnvironmentOutlined />,
        description: '根据选定的目标人群，AI推荐多个高转化使用场景。',
        color: '#52c41a',
        path: '运营策略 → SKU详情 → 智能文案 → Step 3',
    },
    {
        type: 'smart_copy_generation' as TemplateType,
        label: '智能文案生成',
        icon: <RocketOutlined />,
        description: '结合人群、场景和品牌调性，生成多站点FABE文案和标题。',
        color: '#722ed1',
        path: '运营策略 → SKU详情 → 智能文案 → Step 4',
    },

    // ========== 策略分析类 ==========
    {
        type: 'target_audience',
        label: '目标人群分析',
        icon: <BarChartOutlined />,
        description: '精准分析产品的目标受众群体。',
        color: '#1890ff',
        path: '运营策略 → SKU详情 → 产品策略',
    },
    {
        type: 'pain_points',
        label: '用户痛点挖掘',
        icon: <FileTextOutlined />,
        description: '深入挖掘用户在场景中的痛点和需求。',
        color: '#f5222d',
        path: '运营策略 → SKU详情 → 产品策略',
    },
    {
        type: 'usage_scenarios',
        label: '使用场景扩展',
        icon: <BulbOutlined />,
        description: '扩展产品的更多潜在使用场景。',
        color: '#52c41a',
        path: '运营策略 → SKU详情 → 产品策略',
    },
    {
        type: 'primary_usp',
        label: '核心卖点提炼',
        icon: <EditOutlined />,
        description: '提炼产品最核心的独特卖点 (USP)。',
        color: '#faad14',
        path: '运营策略 → SKU详情 → 产品策略',
    },
    {
        type: 'differentiation',
        label: '差异化优势',
        icon: <BarChartOutlined />,
        description: '分析产品与竞品的差异化优势。',
        color: '#13c2c2',
        path: '运营策略 → SKU详情 → 竞品分析',
    },
    {
        type: 'market_positioning',
        label: '市场定位',
        icon: <BarChartOutlined />,
        description: '分析产品在市场中的定位策略。',
        color: '#722ed1',
        path: '运营策略 → SKU详情 → 产品策略',
    },
    {
        type: 'visual_style',
        label: '视觉风格建议',
        icon: <EditOutlined />,
        description: '提供产品视觉呈现的风格建议。',
        color: '#13c2c2',
        path: '运营策略 → SKU详情 → 媒体素材',
    },
    {
        type: 'rufus_analysis',
        label: 'Rufus深度洞察',
        icon: <BulbOutlined />,
        description: '基于Rufus Q&A数据，生成包含GEO策略的深度产品洞察报告。',
        color: '#722ed1',
        path: '运营策略 → SKU详情 → Rufus问答',
    },
];

